/**
 * chartRenderers.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Pure D3 rendering functions for the MarketPulze chart canvas.
 *
 * Exports:
 *   renderCandleLayer(g, chartData, chartType, xScale, yScale)
 *   renderVolumeLayer(g, chartData, xScale, innerH)
 *   renderOverlayLine(g, data, xScale, yScale, color, dash)
 *   renderVolumeProfile(g, vp, yScale, innerW)
 *   renderDrawings(drawings, xScale, yScale, gNode, innerW, candles)
 *   renderMeasure(g, measure, xScale, yScale, innerW)
 *   renderAnalystLayer(g, analystData, yScale, innerW)     — S27K
 *   renderAlertsLayer(g, priceAlerts, yScale)              — S27K
 *
 * Design principles:
 *   1. Stateless — same inputs = same canvas output every call
 *   2. No React state reads or writes
 *   3. Each function owns its SVG group cleanup (.remove() at top)
 *   4. Functions never modify their arguments
 *   5. Adding a new chart type   = one branch in renderCandleLayer only
 *   6. Adding a new drawing tool = one block in renderDrawings only
 *   7. Adding a new overlay      = new exported function, called from useChartRenderer
 *
 * FIX (v8): fmtPrice now imported from chartUtils (not calcIndicators).
 *   calcIndicators is a pure maths file; display formatting doesn't belong there.
 *
 * FIX (v8): renderAnalystLayer and renderAlertsLayer extracted from the
 *   monolith's doRenderChart inline code — they now live here as proper
 *   single-responsibility renderer functions.
 *
 * FIX (v8): anchored_vwap drawing type added to renderDrawings.
 *   Previously it lived in a separate loop inside doRenderChart.
 *   All drawing types now handled in one place.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import * as d3    from 'd3';
import { DRAWING_COLORS, FIB_LEVELS } from '../constants/chartConfig';
import { fmtPrice }                   from '../utils/chartUtils';
import { calcAnchoredVWAP }           from '../lib/calcIndicators';

// ── Candle / Price Series ──────────────────────────────────────────────────────

/**
 * renderCandleLayer
 * Paints the main price series. Supports candlestick, line, mountain, ohlc.
 * Heikin-Ashi and Renko data is transformed before calling — renderer is type-agnostic.
 */
export function renderCandleLayer(g, chartData, chartType, xScale, yScale) {
  g.selectAll('.price-series').remove();
  if (!chartData.length) return;

  const pg   = g.append('g').attr('class', 'price-series');
  const barW = Math.min(24, Math.max(1,
    (xScale.range()[1] - xScale.range()[0]) / chartData.length * 0.65));
  const tickW = Math.max(1, barW * 0.6);

  if (chartType === 'line') {
    const lg = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.close))
      .defined(d => d.close != null);
    pg.append('path')
      .datum(chartData)
      .attr('fill', 'none').attr('stroke', '#00d4ff')
      .attr('stroke-width', 1.5).attr('d', lg);

  } else if (chartType === 'mountain') {
    const yMin = yScale.domain()[0];
    pg.append('path')
      .datum(chartData)
      .attr('fill', 'rgba(0,212,255,0.08)').attr('stroke', 'none')
      .attr('d', d3.area().x(d => xScale(d.date)).y0(yScale(yMin)).y1(d => yScale(d.close)));
    pg.append('path')
      .datum(chartData)
      .attr('fill', 'none').attr('stroke', '#00d4ff').attr('stroke-width', 1.5)
      .attr('d', d3.line().x(d => xScale(d.date)).y(d => yScale(d.close)));

  } else if (chartType === 'ohlc') {
    chartData.forEach(d => {
      const x = xScale(d.date);
      const color = d.close >= d.open ? '#10b981' : '#ef4444';
      pg.append('line').attr('x1', x).attr('y1', yScale(d.high)).attr('x2', x).attr('y2', yScale(d.low)).attr('stroke', color).attr('stroke-width', 1);
      pg.append('line').attr('x1', x - tickW).attr('y1', yScale(d.open)).attr('x2', x).attr('y2', yScale(d.open)).attr('stroke', color).attr('stroke-width', 1);
      pg.append('line').attr('x1', x).attr('y1', yScale(d.close)).attr('x2', x + tickW).attr('y2', yScale(d.close)).attr('stroke', color).attr('stroke-width', 1);
    });

  } else {
    // Candlestick (default) — also used for Heikin-Ashi (same shape, transformed data)
    chartData.forEach(d => {
      const x     = xScale(d.date);
      const isUp  = d.close >= d.open;
      const color = isUp ? '#10b981' : '#ef4444';
      const bTop  = yScale(Math.max(d.open, d.close));
      const bBot  = yScale(Math.min(d.open, d.close));
      pg.append('line').attr('x1', x).attr('y1', yScale(d.high)).attr('x2', x).attr('y2', yScale(d.low)).attr('stroke', color).attr('stroke-width', 1);
      pg.append('rect')
        .attr('x', x - barW / 2).attr('y', bTop)
        .attr('width', barW).attr('height', Math.max(1, bBot - bTop))
        .attr('fill', color).attr('stroke', color).attr('stroke-width', 0.5);
    });
  }
}

// ── Volume Bars ────────────────────────────────────────────────────────────────

/**
 * renderVolumeLayer
 * Volume bars in bottom 18% of chart canvas.
 * Spike detection: bars 2× the 20-bar rolling average get full opacity + amber border.
 */
export function renderVolumeLayer(g, chartData, xScale, innerH) {
  g.selectAll('.vol-g').remove();
  const vg   = g.append('g').attr('class', 'vol-g');
  const maxV = d3.max(chartData, d => d.volume) || 1;
  const volH = innerH * 0.18;
  const yVol = d3.scaleLinear().domain([0, maxV]).range([innerH, innerH - volH]);
  const barW = Math.min(24, Math.max(1,
    (xScale.range()[1] - xScale.range()[0]) / chartData.length * 0.65));

  // Build spike set — O(n) rolling average
  const AVG_WIN  = 20;
  const spikeSet = new Set();
  for (let i = AVG_WIN; i < chartData.length; i++) {
    let sum = 0;
    for (let j = i - AVG_WIN; j < i; j++) sum += chartData[j].volume;
    if (chartData[i].volume >= (sum / AVG_WIN) * 2) spikeSet.add(i);
  }

  vg.selectAll('rect.vol')
    .data(chartData)
    .join('rect')
    .attr('class', 'vol')
    .attr('x',      d => xScale(d.date) - barW / 2)
    .attr('y',      d => yVol(d.volume))
    .attr('width',  barW)
    .attr('height', d => Math.max(0, innerH - yVol(d.volume)))
    .attr('fill', (d, i) => spikeSet.has(i)
      ? (d.close >= d.open ? 'rgba(16,185,129,0.75)' : 'rgba(239,68,68,0.75)')
      : (d.close >= d.open ? 'rgba(16,185,129,0.3)'  : 'rgba(239,68,68,0.3)'))
    .attr('stroke',       (d, i) => spikeSet.has(i) ? 'rgba(245,158,11,0.6)' : 'none')
    .attr('stroke-width', 1);
}

// ── Indicator Overlay Lines ────────────────────────────────────────────────────

/**
 * renderOverlayLine
 * Renders a single indicator line (SMA, EMA, BB band, etc.) as an SVG path.
 */
export function renderOverlayLine(g, data, xScale, yScale, color, dash) {
  if (!data || !data.length) return;
  const lg = d3.line()
    .x(d => xScale(new Date(d.time * 1000)))
    .y(d => yScale(d.value))
    .defined(d => d.value != null);
  g.append('path')
    .datum(data)
    .attr('fill', 'none')
    .attr('stroke', color)
    .attr('stroke-width', 1.5)
    .attr('stroke-dasharray', dash ? dash.join(',') : null)
    .attr('d', lg);
}

// ── Volume Profile ─────────────────────────────────────────────────────────────

/**
 * renderVolumeProfile
 * Horizontal volume bars on right side of chart. POC bar highlighted amber.
 */
export function renderVolumeProfile(g, vp, yScale, innerW) {
  if (!vp || !vp.levels || !vp.levels.length || !vp.maxVol) return;
  const maxBarW = innerW * 0.15;

  vp.levels.forEach(level => {
    if (!level.volume) return;
    const barW = (level.volume / vp.maxVol) * maxBarW;
    const yTop = yScale(level.priceTop);
    const yBot = yScale(level.priceBottom);
    if (isNaN(yTop) || isNaN(yBot)) return;
    const ry    = Math.min(yTop, yBot);
    const rh    = Math.max(1, Math.abs(yBot - yTop));
    const isPOC = Math.abs(level.price - vp.pocPrice) < 0.0001;

    if (isPOC) {
      g.append('rect').attr('x', innerW - barW).attr('y', ry)
        .attr('width', barW).attr('height', rh).attr('fill', '#f59e0b').attr('opacity', 0.85);
      g.append('line')
        .attr('x1', 0).attr('y1', ry + rh / 2).attr('x2', innerW - barW).attr('y2', ry + rh / 2)
        .attr('stroke', '#f59e0b').attr('stroke-width', 1).attr('stroke-dasharray', '4,3').attr('opacity', 0.5);
      g.append('text').attr('x', innerW - barW - 3).attr('y', ry + rh / 2 - 2)
        .attr('fill', '#f59e0b').attr('font-size', '9px').attr('text-anchor', 'end')
        .text('POC ' + fmtPrice(vp.pocPrice));
    } else {
      const upRatio = level.volume > 0 ? level.upVol / level.volume : 0.5;
      const upW     = barW * upRatio;
      const downW   = barW - upW;
      if (upW   > 0) g.append('rect').attr('x', innerW - barW).attr('y', ry).attr('width', upW).attr('height', rh).attr('fill', 'rgba(16,185,129,0.25)');
      if (downW > 0) g.append('rect').attr('x', innerW - barW + upW).attr('y', ry).attr('width', downW).attr('height', rh).attr('fill', 'rgba(239,68,68,0.25)');
    }
  });
}

// ── Drawings ───────────────────────────────────────────────────────────────────

/**
 * renderDrawings
 * Renders all user-drawn annotations. Supports:
 *   horizontal, trendline, fibonacci, rectangle, ray, anchored_vwap
 *
 * FIX (v8): anchored_vwap added here — previously a separate loop in
 *   doRenderChart. All drawing types now handled in one function.
 *   Requires candles param for aVWAP calculation.
 *
 * To add a new drawing type:
 *   1. Add the type to DRAWING_COLORS in chartConfig.js
 *   2. Add click handler in useChartRenderer.js
 *   3. Add a .filter().forEach() block here
 *   No other files need to change.
 *
 * @param {Array}    drawings — array of drawing objects
 * @param {Function} xScale  — createDateIndexScale
 * @param {d3.Scale} yScale  — price scale
 * @param {Element}  gNode   — raw DOM node of chart group
 * @param {number}   innerW  — chart canvas width in pixels
 * @param {Array}    candles — raw candle array [{t,o,h,l,c,v}] for aVWAP calc
 */
export function renderDrawings(drawings, xScale, yScale, gNode, innerW, candles = []) {
  if (!gNode || !xScale || !yScale) return;
  const g = d3.select(gNode);
  g.selectAll('.drawing-layer').remove();
  const layer = g.append('g').attr('class', 'drawing-layer');

  // ── Horizontal level ───────────────────────────────────────────────────────
  drawings.filter(d => d.type === 'horizontal').forEach(d => {
    const y = yScale(d.price);
    if (y == null || isNaN(y)) return;
    layer.append('line').attr('x1', 0).attr('y1', y).attr('x2', innerW).attr('y2', y)
      .attr('stroke', d.color || DRAWING_COLORS.horizontal).attr('stroke-width', 1).attr('stroke-dasharray', '5,3');
    layer.append('text').attr('x', innerW - 2).attr('y', y - 3)
      .attr('fill', d.color || DRAWING_COLORS.horizontal).attr('font-size', '9px').attr('text-anchor', 'end')
      .text(fmtPrice(d.price));
  });

  // ── Trendline ──────────────────────────────────────────────────────────────
  drawings.filter(d => d.type === 'trendline' && d.date1 && d.date2).forEach(d => {
    const x1 = xScale(new Date(d.date1 * 1000)), x2 = xScale(new Date(d.date2 * 1000));
    const y1 = yScale(d.price1),                 y2 = yScale(d.price2);
    if ([x1, x2, y1, y2].some(v => v == null || isNaN(v))) return;
    layer.append('line').attr('x1', x1).attr('y1', y1).attr('x2', x2).attr('y2', y2)
      .attr('stroke', d.color || DRAWING_COLORS.trendline)
      .attr('stroke-width', 1.5).attr('stroke-dasharray', '5,3').attr('stroke-linecap', 'round');
  });

  // ── Fibonacci retracement ──────────────────────────────────────────────────
  drawings.filter(d => d.type === 'fibonacci' && d.priceHigh != null && d.priceLow != null).forEach(d => {
    const range = d.priceHigh - d.priceLow;
    if (range <= 0) return;
    FIB_LEVELS.forEach(level => {
      const levelPrice = d.priceHigh - range * level.ratio;
      const y = yScale(levelPrice);
      if (y == null || isNaN(y)) return;
      layer.append('line').attr('x1', 0).attr('y1', y).attr('x2', innerW).attr('y2', y)
        .attr('stroke', level.color).attr('stroke-width', level.strokeW).attr('stroke-dasharray', '4,3');
      layer.append('text').attr('x', innerW - 2).attr('y', y - 3)
        .attr('fill', level.color).attr('font-size', '9px').attr('text-anchor', 'end')
        .text(fmtPrice(levelPrice) + ' ' + level.label);
    });
    // Shade the key 38.2–61.8% zone
    const y382 = yScale(d.priceHigh - range * 0.382);
    const y618 = yScale(d.priceHigh - range * 0.618);
    if (y382 != null && y618 != null && !isNaN(y382) && !isNaN(y618)) {
      layer.append('rect')
        .attr('x', 0).attr('y', Math.min(y382, y618))
        .attr('width', innerW).attr('height', Math.abs(y618 - y382))
        .attr('fill', 'rgba(245,158,11,0.04)');
    }
  });

  // ── Rectangle zone ────────────────────────────────────────────────────────
  drawings.filter(d => d.type === 'rectangle' && d.priceTop != null && d.priceBottom != null && d.date1 && d.date2).forEach(d => {
    const x1   = xScale(new Date(d.date1 * 1000)), x2 = xScale(new Date(d.date2 * 1000));
    const yTop = yScale(d.priceTop),               yBot = yScale(d.priceBottom);
    if ([x1, x2, yTop, yBot].some(v => v == null || isNaN(v))) return;
    const rx  = Math.min(x1, x2), ry = Math.min(yTop, yBot);
    const rw  = Math.abs(x2 - x1), rh = Math.abs(yBot - yTop);
    if (rw < 2 || rh < 2) return;
    const col = d.color || DRAWING_COLORS.rectangle;
    layer.append('rect').attr('x', rx).attr('y', ry).attr('width', rw).attr('height', rh)
      .attr('fill', col + '18').attr('stroke', col).attr('stroke-width', 1).attr('stroke-dasharray', '4,3');
    layer.append('text').attr('x', rx + rw + 3).attr('y', ry + 10).attr('fill', col).attr('font-size', '9px').text(fmtPrice(d.priceTop));
    layer.append('text').attr('x', rx + rw + 3).attr('y', ry + rh - 2).attr('fill', col).attr('font-size', '9px').text(fmtPrice(d.priceBottom));
  });

  // ── Ray ───────────────────────────────────────────────────────────────────
  drawings.filter(d => d.type === 'ray' && d.date1).forEach(d => {
    const x1 = xScale(new Date(d.date1 * 1000));
    const y  = yScale(d.price);
    if (isNaN(x1) || isNaN(y)) return;
    const cx = Math.max(0, x1);
    layer.append('line').attr('x1', cx).attr('y1', y).attr('x2', innerW).attr('y2', y)
      .attr('stroke', d.color || DRAWING_COLORS.ray).attr('stroke-width', 1).attr('stroke-dasharray', '5,3');
    layer.append('text').attr('x', innerW - 2).attr('y', y - 3)
      .attr('fill', d.color || DRAWING_COLORS.ray).attr('font-size', '9px').attr('text-anchor', 'end')
      .text('→ ' + fmtPrice(d.price));
  });

  // ── Anchored VWAP ─────────────────────────────────────────────────────────
  // FIX (v8): moved from separate loop in doRenderChart into renderDrawings.
  // All drawing types now handled in one function.
  // Requires candles param to calculate the VWAP from the anchor point forward.
  drawings.filter(d => d.type === 'anchored_vwap' && d.anchorDate).forEach(d => {
    if (!candles.length) return;
    const avData = calcAnchoredVWAP(candles, d.anchorDate);
    if (!avData.length) return;
    // Draw the VWAP line
    const lg = d3.line()
      .x(pt => xScale(new Date(pt.time * 1000)))
      .y(pt => yScale(pt.value))
      .defined(pt => pt.value != null);
    layer.append('path')
      .datum(avData)
      .attr('fill', 'none')
      .attr('stroke', d.color || DRAWING_COLORS.anchored_vwap)
      .attr('stroke-width', 1.5)
      .attr('d', lg);
    // Anchor dot + label at origin point
    const ax = xScale(new Date(d.anchorDate * 1000));
    const ay = d.anchorPrice ? yScale(d.anchorPrice) : null;
    if (!isNaN(ax) && ay != null && !isNaN(ay)) {
      layer.append('circle').attr('cx', ax).attr('cy', ay).attr('r', 4)
        .attr('fill', d.color || DRAWING_COLORS.anchored_vwap).attr('opacity', 0.85);
      layer.append('text').attr('x', ax + 7).attr('y', ay - 3)
        .attr('fill', d.color || DRAWING_COLORS.anchored_vwap).attr('font-size', '9px')
        .text('aVWAP');
    }
  });
}

// ── Price Measure Tool ─────────────────────────────────────────────────────────

/**
 * renderMeasure
 * Price/percentage/bar-count measurement overlay.
 */
export function renderMeasure(g, m, xScale, yScale, innerW) {
  g.selectAll('*').remove();
  if (!m || !m.price1 || !m.price2) return;

  const y1   = yScale(m.price1), y2 = yScale(m.price2);
  if (isNaN(y1) || isNaN(y2)) return;

  const yTop  = Math.min(y1, y2), yBot = Math.max(y1, y2), midY = (yTop + yBot) / 2;
  const delta = m.price2 - m.price1;
  const pct   = (delta / m.price1) * 100;
  const color = delta >= 0 ? '#10b981' : '#ef4444';
  const bx    = innerW - 2;

  // Bracket
  g.append('line').attr('x1', bx).attr('y1', yTop).attr('x2', bx).attr('y2', yBot).attr('stroke', color).attr('stroke-width', 1.5);
  g.append('line').attr('x1', bx - 6).attr('y1', yTop).attr('x2', bx).attr('y2', yTop).attr('stroke', color).attr('stroke-width', 1.5);
  g.append('line').attr('x1', bx - 6).attr('y1', yBot).attr('x2', bx).attr('y2', yBot).attr('stroke', color).attr('stroke-width', 1.5);

  // Ghost line during drag
  if (m.ghostY != null) {
    g.append('line').attr('x1', 0).attr('y1', m.ghostY).attr('x2', innerW).attr('y2', m.ghostY)
      .attr('stroke', '#00d4ff').attr('stroke-width', 0.5).attr('stroke-dasharray', '3,3').attr('opacity', 0.5);
  }

  // Label box
  const priceStr = (delta >= 0 ? '+' : '') + '$' + Math.abs(delta).toFixed(2);
  const pctStr   = ' (' + (pct >= 0 ? '+' : '') + pct.toFixed(2) + '%)';
  const barStr   = m.barCount != null ? m.barCount + ' bar' + (m.barCount !== 1 ? 's' : '') : '';
  const lx = bx - 10;
  g.append('rect').attr('x', lx - 90).attr('y', midY - 18).attr('width', 90).attr('height', 30)
    .attr('fill', 'rgba(5,13,26,0.88)').attr('rx', 4).attr('stroke', color).attr('stroke-width', 0.5);
  g.append('text').attr('x', lx - 6).attr('y', midY - 4)
    .attr('fill', color).attr('font-size', '10px').attr('font-weight', '700').attr('text-anchor', 'end')
    .text(priceStr + pctStr);
  g.append('text').attr('x', lx - 6).attr('y', midY + 9)
    .attr('fill', '#8b8ba7').attr('font-size', '9px').attr('text-anchor', 'end').text(barStr);

  if (m.dot1Y != null) {
    g.append('circle').attr('cx', 0).attr('cy', m.dot1Y).attr('r', 3)
      .attr('fill', '#00d4ff').attr('opacity', 0.8);
  }
}

// ── Analyst Price Target Layer (S27K) ──────────────────────────────────────────

/**
 * renderAnalystLayer
 * Renders the analyst consensus price target overlay.
 * Shows a dashed purple line at PT, a consensus pill (Buy N / Hold N / Sell N),
 * and a shaded zone between targetLow and targetHigh.
 *
 * FIX (v8): extracted from doRenderChart inline code into its own function.
 * Called from useChartRenderer.js after the main candle/overlay render.
 *
 * @param {d3.Selection} g            — the analyst-layer D3 group (already selected)
 * @param {Object|null}  analystData  — {priceTarget, consensus, targetHigh, targetLow}
 * @param {d3.Scale}     yScale       — price scale
 * @param {number}       innerW       — chart canvas width
 */
export function renderAnalystLayer(g, analystData, yScale, innerW) {
  g.selectAll('*').remove();
  if (!analystData?.priceTarget) return;

  const pt = analystData.priceTarget;
  const y  = yScale(pt);
  if (isNaN(y)) return;

  // Dashed price target line
  g.append('line')
    .attr('x1', 0).attr('y1', y).attr('x2', innerW).attr('y2', y)
    .attr('stroke', '#a78bfa').attr('stroke-width', 1.2)
    .attr('stroke-dasharray', '6,3').attr('opacity', 0.9);

  // Consensus pill label
  const cons  = analystData.consensus;
  const bull  = cons ? (cons.strongBuy  + cons.buy)  : null;
  const bear  = cons ? (cons.sell       + cons.strongSell) : null;
  const hold  = cons ? cons.hold : null;
  const label = 'PT ' + fmtPrice(pt) + (cons ? ' · B' + bull + ' H' + hold + ' S' + bear : '');
  const lw    = Math.min(label.length * 6.2 + 14, 220);

  g.append('rect').attr('x', 6).attr('y', y - 14).attr('width', lw).attr('height', 14)
    .attr('fill', 'rgba(167,139,250,0.12)').attr('rx', 3)
    .attr('stroke', 'rgba(167,139,250,0.28)').attr('stroke-width', 0.5);
  g.append('text').attr('x', 10).attr('y', y - 3)
    .attr('fill', '#a78bfa').attr('font-size', '9px').attr('font-weight', '700')
    .text(label);

  // High/low target shaded zone
  if (analystData.targetHigh && analystData.targetLow) {
    const yH = yScale(analystData.targetHigh);
    const yL = yScale(analystData.targetLow);
    if (!isNaN(yH) && !isNaN(yL)) {
      g.append('rect')
        .attr('x', 0).attr('y', Math.min(yH, yL))
        .attr('width', innerW).attr('height', Math.abs(yL - yH))
        .attr('fill', 'rgba(167,139,250,0.04)');
    }
  }
}

// ── Price Alerts Layer (S27K) ──────────────────────────────────────────────────

/**
 * renderAlertsLayer
 * Renders member's active price alerts as dashed lines with direction labels.
 * Only called when priceAlerts.length > 0 (gate in useChartRenderer.js).
 *
 * FIX (v8): extracted from doRenderChart inline code into its own function.
 *
 * @param {d3.Selection} g           — the alerts-layer D3 group (already selected)
 * @param {Array}        priceAlerts — [{targetPrice, direction: "above"|"below"}]
 * @param {d3.Scale}     yScale      — price scale
 */
export function renderAlertsLayer(g, priceAlerts, yScale) {
  g.selectAll('*').remove();
  if (!priceAlerts?.length) return;

  priceAlerts.forEach(alert => {
    const y   = yScale(alert.targetPrice);
    if (isNaN(y)) return;
    const col = alert.direction === 'above' ? '#10b981' : '#ef4444';

    g.append('line')
      .attr('x1', 0).attr('y1', y).attr('x2', 10000).attr('y2', y) // extend past innerW — clipped by SVG
      .attr('stroke', col).attr('stroke-width', 1).attr('stroke-dasharray', '3,4')
      .attr('opacity', 0.65);
    g.append('text')
      .attr('x', 6).attr('y', y - 3)
      .attr('fill', col).attr('font-size', '9px')
      .text('⚑ ' + fmtPrice(alert.targetPrice) + ' ' + alert.direction);
  });
}
